import { useEffect, useState } from "react";
import { AlertTriangle, Check, Loader2, X } from "lucide-react";
import {
  LISTING_FIELDS,
  changedListingFields,
  type AdminListingChangeDetailDTO,
  type ListingChangePayload,
  type ListingChangeStatus,
  type ListingField,
  type MarketplaceListingLiveDTO,
} from "@colab/shared";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { useAuth } from "@/lib/auth-context";
import { cn, formatDate, formatRelative } from "@/lib/utils";
import { useApproveListingChange } from "./use-approve-listing-change";
import { DeclineListingDialog } from "./decline-listing-dialog";
import { useListingChange } from "./use-listing-change";

// Right-side drawer for one listing-change request, opened via `?request=`
// on the queue page (submission-drawer convention: mounted unconditionally,
// inner null-guard so idle page mounts stay free).
//
// Body renders a side-by-side "Current (live)" vs "Proposed" diff over the
// seven LISTING_FIELDS; changed rows highlight via changedListingFields().
// When the linked marketplace row is gone (`live: null`) the diff collapses
// to a proposed-only column behind a stale warning — nothing to diff
// against, and approve is blocked anyway.
//
// Footer: Approve / Decline for pending requests when the viewer holds
// `listings.review`; decided requests show reviewed_at + the review note
// (MDF request-drawer style).

const FIELD_LABELS: Record<ListingField, string> = {
  name: "Name",
  short_description: "Short description",
  full_description: "Full description",
  logo_url: "Logo",
  hero_image_url: "Hero image",
  website_url: "Website URL",
  documentation_url: "Documentation URL",
};

const IMAGE_FIELDS: ReadonlySet<ListingField> = new Set([
  "logo_url",
  "hero_image_url",
]);

const STATUS_LABEL: Record<ListingChangeStatus, string> = {
  pending: "Pending",
  approved: "Approved",
  declined: "Declined",
};

export function ListingChangeStatusBadge({
  status,
}: {
  status: ListingChangeStatus;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium",
        status === "pending" &&
          "bg-amber-100 text-amber-900 dark:bg-amber-500/10 dark:text-amber-300",
        status === "approved" &&
          "bg-emerald-100 text-emerald-900 dark:bg-emerald-500/10 dark:text-emerald-300",
        status === "declined" &&
          "bg-rose-100 text-rose-900 dark:bg-rose-500/10 dark:text-rose-300",
      )}
    >
      {STATUS_LABEL[status]}
    </span>
  );
}

export function ListingChangeDrawer({
  requestId,
  configured,
  onClose,
}: {
  requestId: string | null;
  /** From the queue list payload — false when MARKETPLACE_* secrets are
   *  unset, which blocks approvals server-side. */
  configured: boolean;
  onClose: () => void;
}) {
  const open = requestId !== null;
  const query = useListingChange(requestId);

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 overflow-hidden p-0 sm:max-w-[640px]"
      >
        {!requestId ? null : query.isLoading ? (
          <DrawerSkeleton />
        ) : query.isError || !query.data ? (
          <div className="p-6">
            <SheetHeader>
              <SheetTitle>Couldn&apos;t load this request</SheetTitle>
              <SheetDescription>
                {query.error?.message ?? "The request may no longer exist."}
              </SheetDescription>
            </SheetHeader>
          </div>
        ) : (
          <DrawerBody
            detail={query.data}
            requestId={requestId}
            configured={configured}
          />
        )}
      </SheetContent>
    </Sheet>
  );
}

function DrawerSkeleton() {
  return (
    <div className="space-y-4 p-6">
      <Skeleton className="h-5 w-2/3" />
      <Skeleton className="h-3 w-1/3" />
      <Separator className="my-3" />
      <Skeleton className="h-24 w-full" />
      <Skeleton className="h-24 w-full" />
    </div>
  );
}

function DrawerBody({
  detail,
  requestId,
  configured,
}: {
  detail: AdminListingChangeDetailDTO;
  requestId: string;
  configured: boolean;
}) {
  const { request, live } = detail;
  const session = useAuth().session;
  const canReview = !!session?.permissions.includes("listings.review");
  const approve = useApproveListingChange();
  const [declineOpen, setDeclineOpen] = useState(false);

  // Close the inner dialog if the drawer switches to a different request.
  useEffect(() => {
    setDeclineOpen(false);
  }, [requestId]);

  const listingLabel = request.listing_name ?? `#${request.integration_id}`;
  const changed = live
    ? changedListingFields(livePayload(live), request.payload)
    : [];
  const approveBlocked = request.stale || live === null || !configured;

  return (
    <>
      <SheetHeader className="border-b p-6 pb-5 text-left">
        <SheetTitle className="font-display text-lg font-semibold tracking-tight">
          {request.partner_name}
        </SheetTitle>
        <SheetDescription className="mt-1 flex flex-wrap items-center gap-2 text-xs">
          <ListingChangeStatusBadge status={request.status} />
          <span className="text-muted-foreground">
            · {listingLabel} · Submitted {formatDate(request.created_at)} (
            {formatRelative(request.created_at)})
          </span>
        </SheetDescription>
      </SheetHeader>

      <div className="flex-1 space-y-6 overflow-y-auto px-6 py-5">
        {live === null ? (
          <>
            <div className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-900 dark:text-amber-300">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <p>
                The linked marketplace listing no longer exists, so there is
                no live version to compare against. This request can&apos;t be
                approved — re-link the partner to a listing first.
              </p>
            </div>
            <section>
              <h3 className="mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Proposed
              </h3>
              <div className="divide-y divide-border/60 rounded-xl border bg-card">
                {LISTING_FIELDS.map((field) => (
                  <div key={field} className="px-4 py-3">
                    <p className="mb-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                      {FIELD_LABELS[field]}
                    </p>
                    <FieldValue field={field} value={request.payload[field]} />
                  </div>
                ))}
              </div>
            </section>
          </>
        ) : (
          <section>
            <div className="mb-2 grid grid-cols-2 gap-4">
              <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Current (live)
              </h3>
              <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Proposed
              </h3>
            </div>
            <div className="divide-y divide-border/60 rounded-xl border bg-card">
              {LISTING_FIELDS.map((field) => {
                const isChanged = changed.includes(field);
                return (
                  <div
                    key={field}
                    className={cn(
                      "px-4 py-3",
                      isChanged && "border-l-2 border-l-accent bg-accent/5",
                    )}
                  >
                    <p className="mb-1 flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                      {FIELD_LABELS[field]}
                      {isChanged && (
                        <span className="rounded bg-accent/15 px-1 py-px text-[9px] font-semibold normal-case tracking-normal text-accent">
                          Changed
                        </span>
                      )}
                    </p>
                    <div className="grid grid-cols-2 gap-4">
                      <FieldValue field={field} value={live[field]} muted />
                      <FieldValue field={field} value={request.payload[field]} />
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {request.status !== "pending" && (
          <section>
            <h3 className="mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
              {request.status === "approved" ? "Approved" : "Declined"}
              {request.reviewed_at
                ? ` ${formatDate(request.reviewed_at)}`
                : ""}
            </h3>
            {request.review_note ? (
              <p
                className={cn(
                  "rounded-md border p-3 text-sm italic",
                  request.status === "declined"
                    ? "border-destructive/30 bg-destructive/5"
                    : "bg-muted/30",
                )}
              >
                &ldquo;{request.review_note}&rdquo;
              </p>
            ) : (
              <p className="text-sm text-muted-foreground">
                No note was left for the partner.
              </p>
            )}
          </section>
        )}
      </div>

      {request.status === "pending" && canReview && (
        <footer className="space-y-2 border-t bg-card p-4">
          {approveBlocked && (
            <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500" />
              {!configured
                ? "Marketplace connection not configured — approvals are blocked."
                : "The linked marketplace listing no longer exists — approval is blocked until the partner is re-linked."}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setDeclineOpen(true)}
              disabled={approve.isPending}
            >
              <X className="mr-1 h-3.5 w-3.5" />
              Decline…
            </Button>
            <Button
              size="sm"
              onClick={() => approve.mutate({ id: requestId })}
              disabled={approve.isPending || approveBlocked}
            >
              {approve.isPending ? (
                <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
              ) : (
                <Check className="mr-1 h-3.5 w-3.5" />
              )}
              Approve
            </Button>
          </div>
        </footer>
      )}

      <DeclineListingDialog
        requestId={requestId}
        open={declineOpen}
        onOpenChange={setDeclineOpen}
      />
    </>
  );
}

// The live DTO carries id/slug/status/updated_at on top of the seven
// editable fields; changedListingFields() wants the bare payload shape.
function livePayload(live: MarketplaceListingLiveDTO): ListingChangePayload {
  return {
    name: live.name,
    short_description: live.short_description,
    full_description: live.full_description,
    logo_url: live.logo_url,
    hero_image_url: live.hero_image_url,
    website_url: live.website_url,
    documentation_url: live.documentation_url,
  };
}

function FieldValue({
  field,
  value,
  muted = false,
}: {
  field: ListingField;
  value: string | null;
  muted?: boolean;
}) {
  if (value === null || value === "") {
    return <p className="text-sm text-muted-foreground">—</p>;
  }
  if (IMAGE_FIELDS.has(field)) {
    return (
      <img
        src={value}
        alt={FIELD_LABELS[field]}
        className="h-12 max-w-[160px] rounded border object-contain"
      />
    );
  }
  return (
    <p
      className={cn(
        "whitespace-pre-wrap break-words text-sm",
        field === "full_description" && "line-clamp-6",
        field === "short_description" && "line-clamp-3",
        muted && "text-muted-foreground",
      )}
    >
      {value}
    </p>
  );
}
